import {
  EActionBoxCodeEvent,
  ESessionPhase,
  ESocketEvents,
  IBoxCore,
  IGameCore,
  ISessionBodyPlayerPublic,
  ISessionCore,
  TBoxBodyPublic,
  TGameBodyPublic,
  TGamePublic,
} from '@lbs/shared-rp-types'
import { RussianPokerSocketApi } from 'machines/RussianPoker'
import { makeAutoObservable } from 'mobx'
import { toast } from 'react-toastify'
import { getErrorMessage } from '../../utils/errorTranslation'
import { BoxStore } from './Box.store'
import { UIStore } from './UI.store'

const VALID_SEAT_NUMBERS = [1, 2]

const isActionResolutionPatch = (data: Partial<TBoxBodyPublic>): boolean => {
  // Патчи, подтверждающие завершение action-потока для бокса.
  // Используем только для снятия submitting-lock в BoxUIStore.
  return (
    'pendingCardsRequest' in data ||
    'insurance' in data ||
    'switchCards' in data ||
    'buySixCard' in data
  )
}

export class RussianPokerStore {
  gameId: string = ''

  game!: TGamePublic

  session!: ISessionBodyPlayerPublic

  boxes: Array<BoxStore> = []

  deposit: number = 0

  uiStore: UIStore

  selectedChip: number = 0

  getSocket!: () => RussianPokerSocketApi

  constructor({
    gameId,
    getSocket,
  }: {
    gameId: string
    getSocket: () => RussianPokerSocketApi
  }) {
    makeAutoObservable(this, { getSocket: false })

    this.uiStore = new UIStore()
    this.gameId = gameId
    this.getSocket = getSocket
  }

  get stakedBoxes(): Array<{
    seatNumber: number
    ante: number
  }> {
    return this.boxes
      .filter((box) => box.hasStake)
      .map((box) => {
        return {
          seatNumber: box.seatNumber,
          ante: box.ante,
        }
      })
  }

  get isReadyToGame(): boolean {
    return (
      this.boxes.some((box) => Boolean(box.ante)) &&
      this.session.phase === ESessionPhase.BETTING
    )
  }

  get isFinishPhase(): boolean {
    return this.session.phase === ESessionPhase.FINISHED
  }

  get isBettingStatus(): boolean {
    return this.session.phase === ESessionPhase.BETTING
  }

  patchStore = (data: Partial<RussianPokerStore>): void => {
    Object.assign(this, data)
  }

  patchGame = (payload: {
    target: IGameCore
    data: Partial<TGameBodyPublic>
  }): void => {
    Object.assign(this.game, payload.data)
  }

  patchSession = (payloadSession: {
    target: ISessionCore
    data: Partial<ISessionBodyPlayerPublic>
  }): void => {
    Object.assign(this.session, payloadSession.data)
  }

  patchBox = (payloadBox: {
    success: boolean
    target: IBoxCore
    data: Partial<TBoxBodyPublic>
    error?: {
      event: string
      code: string
      message: string
    }
  }): void => {
    if (!payloadBox.success) {
      if (payloadBox.error) {
        this.toastWarning(getErrorMessage(payloadBox.error.code))
      }

      // Снимаем транзитный lock submitting для затронутого бокса.
      // Toast уже показан выше; здесь только разблокируем UI.
      const errorBox = this.boxes.find(
        (box) => box.seatNumber === payloadBox.target.seatNumber
      )
      errorBox?.ui.onActionError()

      return
    }

    const targetBox = this.boxes.find((box) => {
      return box.seatNumber === payloadBox.target.seatNumber
    })

    if (!targetBox) {
      const { seatNumber } = payloadBox.target

      if (!VALID_SEAT_NUMBERS.includes(seatNumber)) {
        console.warn('Invalid seatNumber:', seatNumber)
        return
      }

      const newBox = new BoxStore(
        {
          seatNumber,
          ...payloadBox.data,
        } as TBoxBodyPublic,
        this
      )
      this.boxes.push(newBox)
      this.boxes.sort((a, b) => a.seatNumber - b.seatNumber)
      return
    }

    targetBox.patch(payloadBox.data)

    // Снимаем submitting-lock только по релевантному success-патчу действия.
    if (isActionResolutionPatch(payloadBox.data)) {
      targetBox.markActionPatchResolved()
    }
  }

  setDeposit = (deposit: number): void => {
    this.deposit = deposit
  }

  setBoxes = (boxes: Array<TBoxBodyPublic>): void => {
    this.boxes.forEach((box) => box.ui.dispose())

    this.boxes = []

    boxes
      .sort((a, b) => a.seatNumber - b.seatNumber)
      .forEach((box) => {
        this.boxes.push(new BoxStore(box, this))
      })
  }

  disposeStores = (): void => {
    this.boxes.forEach((box) => box.ui.dispose())
  }

  emitBox = (payload: {
    event: EActionBoxCodeEvent
    target: IBoxCore
    data?: object
  }): void => {
    const { event, data, target } = payload
    this.getSocket().emit(ESocketEvents.ACTION_BOX, { target, event, data })
  }

  toastWarning = (txt: string) => {
    toast.warning(txt, { containerId: 'prp2box' })
  }

  toastSuccess = (txt: string) => {
    toast.success(txt, { containerId: 'prp2box' })
  }

  toastError = (txt: string) => {
    toast.error(txt, { containerId: 'prp2box' })
  }
}
